import React, { useState, useMemo, useEffect, useRef } from 'react';
import { CALCULATORS, CATEGORIES_CONFIG, DEV_TOOLS } from './constants';
import { Category } from './types';
import { CalculatorCard } from './components/CalculatorCard';
import { ConstructionPage } from './components/ConstructionPage';
import { ScrollReveal } from './components/ScrollReveal';
import { BackToTop } from './components/BackToTop';
import { Icon } from './components/Icon';
import { CookieConsent } from './components/CookieConsent';
import { ThemeToggle } from './components/ThemeToggle';
import { ContactPage } from './components/pages/ContactPage';
import { PrivacyPage } from './components/pages/PrivacyPage';
import { AboutPage } from './components/pages/AboutPage';
import { TermsPage } from './components/pages/TermsPage';

// Importar todos os módulos de calculadora
// Importar módulos consolidados
import { FinanceModule } from './components/calculators/FinanceModule';
import { HealthModule } from './components/calculators/HealthModule';
import { WorkModule } from './components/calculators/WorkModule';
import { TimeModule } from './components/calculators/TimeModule';
import { SimulatorsModule } from './components/calculators/SimulatorsModule';
import { PlanningModule } from './components/calculators/PlanningModule';
import { ConsumptionModule } from './components/calculators/ConsumptionModule';
import { MathModule } from './components/calculators/MathModule';
import { AllCalculatorsModule } from './components/calculators/AllCalculatorsModule';
import { DevToolsModule } from './components/calculators/DevToolsModule';

const App: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Todos');
  const [selectedCalculator, setSelectedCalculator] = useState<string | null>(null);
  const [activePage, setActivePage] = useState<'home' | 'contact' | 'privacy' | 'about' | 'terms'>('home');
  const [favoriteSlugs, setFavoriteSlugs] = useState<string[]>(() => {
    const saved = localStorage.getItem('calc-pro-favorites');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Erro ao carregar favoritos', e);
      }
    }
    return [];
  });
  const suppressUrlSyncRef = useRef(false);

  const parseSlugFromPathname = (pathname: string): string | null => {
    const match = pathname.match(/^\/calculadora\/([^/]+)\/?$/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  };

  const openCalculator = (slug: string) => {
    setSelectedCalculator(slug);
    setActivePage('home'); // Ensure we are on the home page when opening a calculator
  };

  const closeCalculator = () => {
    setSelectedCalculator(null);
  };

  // Deep linking: ler URL ao carregar e reagir a back/forward
  useEffect(() => {
    const initialSlug = parseSlugFromPathname(window.location.pathname);
    if (initialSlug) {
      const allTools = [...CALCULATORS, ...DEV_TOOLS];
      const exists = allTools.some(c => c.slug === initialSlug);
      if (exists) {
        suppressUrlSyncRef.current = true;
        setSelectedCalculator(initialSlug);
      } else {
        window.history.replaceState({}, '', '/');
      }
    }

    const handlePopState = () => {
      const slug = parseSlugFromPathname(window.location.pathname);
      const allTools = [...CALCULATORS, ...DEV_TOOLS];
      const exists = slug ? allTools.some(c => c.slug === slug) : true;
      suppressUrlSyncRef.current = true;
      setSelectedCalculator(exists ? slug : null);
      if (!exists) {
        window.history.replaceState({}, '', '/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Favoritos: Salvar no LocalStorage
  useEffect(() => {
    localStorage.setItem('calc-pro-favorites', JSON.stringify(favoriteSlugs));
  }, [favoriteSlugs]);

  const toggleFavorite = (slug: string) => {
    setFavoriteSlugs(prev =>
      prev.includes(slug)
        ? prev.filter(s => s !== slug)
        : [...prev, slug]
    );
  };

  // Manter URL sincronizada com o estado quando abrir/fechar calculadora
  useEffect(() => {
    if (suppressUrlSyncRef.current) {
      suppressUrlSyncRef.current = false;
      return;
    }

    const nextPath = selectedCalculator ? `/calculadora/${encodeURIComponent(selectedCalculator)}` : '/';
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, '', nextPath);
    }
  }, [selectedCalculator]);

  const filteredCalculators = useMemo(() => {
    // Combinar calculadoras normais com ferramentas Dev
    const allTools = [...CALCULATORS, ...DEV_TOOLS];

    return allTools.filter((calc) => {
      const matchesSearch = calc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        calc.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = activeCategory === 'Todos' || calc.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, activeCategory]);

  const popularCalculators = useMemo(() =>
    CALCULATORS.filter(c => c.isPopular).slice(0, 8),
    []);

  const favoriteCalculators = useMemo(() => {
    const allTools = [...CALCULATORS, ...DEV_TOOLS];
    return allTools.filter(c => favoriteSlugs.includes(c.slug));
  }, [favoriteSlugs]);

  useEffect(() => {
    if (selectedCalculator) {
      const calc = CALCULATORS.find(c => c.slug === selectedCalculator);
      document.title = calc ? `${calc.title} - Calculadora PRO` : 'Calculadora PRO';
    } else if (activePage === 'contact') {
      document.title = 'Contato - Calculadora PRO';
    } else if (activePage === 'privacy') {
      document.title = 'Política de Privacidade - Calculadora PRO';
    } else if (activePage === 'about') {
      document.title = 'Sobre Nós - Calculadora PRO';
    } else if (activePage === 'terms') {
      document.title = 'Termos de Uso - Calculadora PRO';
    } else {
      document.title = 'Calculadora PRO - O Hub Completo de Ferramentas';
    }
  }, [selectedCalculator, activePage]);

  if (activePage === 'contact') {
    return (
      <>
        <ContactPage onBack={() => setActivePage('home')} />
        <CookieConsent onNavigatePrivacy={() => setActivePage('privacy')} />
      </>
    );
  }

  if (activePage === 'privacy') {
    return (
      <>
        <PrivacyPage onBack={() => setActivePage('home')} />
        <CookieConsent onNavigatePrivacy={() => setActivePage('privacy')} />
      </>
    );
  }

  if (activePage === 'about') {
    return (
      <>
        <AboutPage onBack={() => setActivePage('home')} />
        <CookieConsent onNavigatePrivacy={() => setActivePage('privacy')} />
      </>
    );
  }

  if (activePage === 'terms') {
    return (
      <>
        <TermsPage onBack={() => setActivePage('home')} />
        <CookieConsent onNavigatePrivacy={() => setActivePage('privacy')} />
      </>
    );
  }

  if (selectedCalculator) {
    const allTools = [...CALCULATORS, ...DEV_TOOLS];
    const calcData = allTools.find(c => c.slug === selectedCalculator);

    if (!calcData) return null;

    const commonProps = {
      calculator: calcData,
      onBack: closeCalculator,
      isFavorite: favoriteSlugs.includes(calcData.slug),
      onToggleFavorite: () => toggleFavorite(calcData.slug)
    };

    // Roteamento simplificado baseado na categoria
    switch (calcData.category) {
      case Category.Financeiro:
        return <FinanceModule {...commonProps} />;
      case Category.Saude:
        return <HealthModule {...commonProps} />;
      case Category.Trabalhista:
        return <WorkModule {...commonProps} />;
      case Category.Tempo:
        return <TimeModule {...commonProps} />;
      case Category.Simuladores:
        return <SimulatorsModule {...commonProps} />;
      case Category.Planejamento:
        return <PlanningModule {...commonProps} />;
      case Category.Consumo:
        return <ConsumptionModule {...commonProps} />;
      case Category.Matematica:
        return <MathModule {...commonProps} />;
      case Category.DevTools:
        return <DevToolsModule {...commonProps} />;
      default:
        return <AllCalculatorsModule {...commonProps} />;
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-theme-bg selection:bg-indigo-100 selection:text-indigo-900 scroll-smooth transition-colors duration-300">
      <header className="pt-16 pb-10 px-6 relative z-10 border-b border-theme-border/50">
        <div className="max-w-5xl mx-auto flex flex-col items-center">
          <div className="w-full flex justify-end mb-4">
            <ThemeToggle />
          </div>
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-accent-gradient rounded-2xl flex items-center justify-center text-white shadow-accent animate-float">
              <Icon name="divide" className="w-7 h-7" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-theme-text mb-6 flex items-center justify-center gap-2">
            Calculadora<span className="bg-clip-text text-transparent bg-accent-gradient">PRO</span>
          </h1>
          <p className="text-lg text-theme-muted max-w-2xl mx-auto mb-10 leading-relaxed text-center">
            <span className="font-black text-purple-600 dark:text-emerald-500 transition-colors">Números</span> sem complicação.
          </p>

          <div className="max-w-2xl mx-auto relative w-full">
            <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none text-slate-400">
              <Icon name="search" className="w-5 h-5" />
            </div>
            <input
              type="text"
              placeholder="O que você deseja calcular?"
              className="w-full bg-theme-card border border-theme-border rounded-2xl py-5 pl-14 pr-10 text-theme-text shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 dark:focus:border-indigo-500 transition-all outline-none text-base font-bold placeholder:text-theme-muted"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      <nav className="md:sticky top-0 z-40 bg-theme-bg/80 backdrop-blur-2xl border-b border-theme-border py-4 px-2 md:px-4 shadow-sm">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center items-center gap-1 md:gap-2">
          <button
            onClick={() => setActiveCategory('Todos')}
            className={`flex px-2.5 md:px-5 py-1.5 md:py-2 rounded-xl transition-all items-center gap-1 md:gap-2 border shadow-sm justify-center uppercase ${activeCategory === 'Todos'
              ? 'bg-theme-card border-indigo-500 text-indigo-600 dark:text-indigo-400 ring-4 ring-indigo-500/10'
              : 'bg-theme-card border-theme-border text-theme-muted hover:border-indigo-400 dark:hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400'}`}
          >
            <span className="text-sm md:text-base">📋</span>
            <span className="text-[8px] md:text-[10px] font-black tracking-widest">TODOS</span>
          </button>
          {CATEGORIES_CONFIG.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex px-2.5 md:px-5 py-1.5 md:py-2 rounded-xl transition-all items-center gap-1 md:gap-2 border shadow-sm justify-center uppercase ${activeCategory === cat.id
                ? 'bg-theme-card border-indigo-500 text-indigo-600 dark:text-indigo-400 ring-4 ring-indigo-500/10'
                : 'bg-theme-card border-theme-border text-theme-muted hover:border-indigo-400 dark:hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400'}`}
            >
              <span className="text-sm md:text-base">{cat.icon}</span>
              <span className="text-[8px] md:text-[10px] font-black tracking-widest">{cat.id}</span>
            </button>
          ))}
        </div>
      </nav>

      <main className="flex-grow max-w-7xl mx-auto w-full px-6 py-12 relative z-10">
        <div className="space-y-24">
          {searchTerm === '' && activeCategory === 'Todos' && favoriteCalculators.length > 0 && (
            <section className="bg-theme-card/40 p-10 rounded-[3rem] border border-theme-border shadow-inner backdrop-blur-sm">
              <div className="flex flex-col mb-10 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Icon name="star" className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <h2 className="text-2xl font-[1000] text-theme-text tracking-tight">Seus Favoritos</h2>
                </div>
                <div className="h-1 w-12 bg-amber-500 rounded-full mx-auto" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {favoriteCalculators.map((calc) => (
                  <CalculatorCard key={calc.id} calc={calc} onSelect={openCalculator} />
                ))}
              </div>
            </section>
          )}

          {searchTerm === '' && activeCategory === 'Todos' && (
            <section>
              <div className="flex flex-col mb-10 text-center">
                <h2 className="text-2xl font-[1000] text-theme-text mb-2 tracking-tight">Populares Agora</h2>
                <div className="h-1 w-12 bg-indigo-600 rounded-full mx-auto" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {popularCalculators.map((calc) => (
                  <CalculatorCard key={calc.id} calc={calc} onSelect={openCalculator} />
                ))}
              </div>
            </section>
          )}

          {CATEGORIES_CONFIG.filter(cat => activeCategory === 'Todos' || activeCategory === cat.id).map((catConfig) => {
            const calcsInCategory = filteredCalculators.filter(c => c.category === catConfig.id);
            if (calcsInCategory.length === 0) return null;

            return (
              <section key={catConfig.id} className="scroll-mt-32">
                <ScrollReveal>
                  <div className="flex items-center gap-5 mb-10">
                    <div className={`w-12 h-12 rounded-2xl ${catConfig.bg} flex items-center justify-center text-2xl shadow-inner border border-theme-border/50`}>
                      {catConfig.icon}
                    </div>
                    <div>
                      <h2 className="text-2xl font-[1000] text-theme-text leading-tight tracking-tight">{catConfig.id}</h2>
                      <p className="text-[9px] font-black text-theme-muted uppercase tracking-[0.2em] mt-1">{calcsInCategory.length} ferramentas prontas</p>
                    </div>
                  </div>
                </ScrollReveal>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {calcsInCategory.map((calc, idx) => (
                    <ScrollReveal key={calc.id} delay={(idx % 4) * 50}>
                      <CalculatorCard calc={calc} onSelect={openCalculator} />
                    </ScrollReveal>
                  ))}
                </div>
              </section>
            );
          })}

          {filteredCalculators.length === 0 && (
            <div className="text-center py-32 bg-theme-card rounded-[3rem] border-2 border-dashed border-theme-border shadow-sm">
              <div className="w-20 h-20 bg-theme-bg rounded-full flex items-center justify-center mx-auto mb-6 text-theme-muted">
                <Icon name="search" className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-black text-theme-text mb-3 tracking-tight">Nenhuma calculadora encontrada</h3>
              <p className="text-theme-muted max-w-sm mx-auto font-medium text-base leading-relaxed">Não encontramos o que você buscava.</p>
              <button
                onClick={() => { setSearchTerm(''); setActiveCategory('Todos'); }}
                className="mt-8 px-10 py-4 bg-accent-gradient text-white rounded-xl font-black shadow-accent hover:shadow-accent-hover transition-all hover:-translate-y-1 active:scale-95 uppercase text-xs tracking-widest"
              >
                Resetar Filtros
              </button>
            </div>
          )}
        </div>
      </main>

      <BackToTop />
      <CookieConsent onNavigatePrivacy={() => setActivePage('privacy')} />

      <footer className="bg-theme-card border-t border-theme-border py-16 px-6 relative z-10 w-full mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-gradient rounded-xl flex items-center justify-center text-white shadow-accent">
              <Icon name="divide" className="w-6 h-6" />
            </div>
            <div>
              <span className="text-xl font-black text-theme-text dark:text-white">CALC.PRO</span>
              <p className="text-xs text-theme-muted font-medium mt-0.5">Ferramentas que facilitam sua vida</p>
            </div>
          </div>

          <div className="flex gap-8 text-sm font-semibold text-theme-muted">
            <button onClick={() => setActivePage('about')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Sobre Nós</button>
            <button onClick={() => setActivePage('privacy')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Privacidade</button>
            <button onClick={() => setActivePage('terms')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Termos</button>
            <button onClick={() => setActivePage('contact')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Contato</button>
          </div>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 6s infinite ease-in-out;
        }
        .cubic-bezier {
          transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div >
  );
};

export default App;
